/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: ProjectRoleList.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-ROLES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * ProjectRoleList - Лесенчатый конвейер ролей с группировкой совместных этапов.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - Лесенчатая раскладка этапов и визуальная группировка совместных ролей.
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракт компонента и парный END_METHOD.
 * END_CHANGE_SUMMARY
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import GroupWorkOutlinedIcon from '@mui/icons-material/GroupWorkOutlined';
import ProjectRoleStageGroup from './ProjectRoleStageGroup';
import { groupRolesIntoStages } from '../projectRoleLayoutUtils';

/* START_METHOD_ProjectRoleList */
/* START_CONTRACT: ProjectRoleList
 * PURPOSE: Отобразить роли в лесенчатом конвейере или заглушку при пустом списке.
 * INPUTS: { roles: ProjectRole[], onEdit: (r)=>void, onDelete: (r)=>void }.
 * OUTPUTS: { JSX.Element - список }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-ROLES
 * END_CONTRACT: ProjectRoleList
 */
export default function ProjectRoleList({ roles, onEdit, onDelete }) {
  if (roles.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          py: { xs: 6, sm: 8 },
          px: 3,
          textAlign: 'center',
          backgroundImage: (theme) =>
            `radial-gradient(ellipse 80% 60% at 50% 0%, ${theme.palette.primary.main}08 0%, transparent 70%)`,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
          }}
        >
          <GroupWorkOutlinedIcon sx={{ fontSize: 28 }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={600}>
          Роли ещё не заданы
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
          Добавьте проектные роли и укажите порядок работы над фичами — так вы увидите конвейер команды.
        </Typography>
      </Paper>
    );
  }

  const stages = groupRolesIntoStages(roles);
  const concurrentStageCount = stages.filter((s) => s.isConcurrent).length;

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            letterSpacing: '0.1em',
            lineHeight: 1.4,
          }}
        >
          Конвейер ролей
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {stages.length} {stages.length === 1 ? 'этап' : stages.length < 5 ? 'этапа' : 'этапов'}
          {concurrentStageCount > 0 && ` · ${concurrentStageCount} совместн.`}
        </Typography>
      </Box>

      {stages.length > 1 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', px: 2, pt: 1.5, pb: 0.5 }}
        >
          Слева вверху — кто начинает работу раньше, вниз и вправо — позже в очереди
        </Typography>
      )}

      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.015),
        }}
      >
        {stages.map((stage) => (
          <ProjectRoleStageGroup
            key={stage.roles.map((r) => r.id).join('-')}
            stage={stage}
            stageIndex={stage.index}
            totalStages={stages.length}
            allRoles={roles}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </Box>
    </Paper>
  );
}
/* END_METHOD_ProjectRoleList */
